// src/pages/teacher/TeacherExpenses.tsx
// מעקב הוצאות — רשימה + סינון + הוספה

import { useState, useEffect } from 'react';
import { Receipt, Plus, X, Filter, Trash2 } from 'lucide-react';
import { teacherApi } from '../../services/api';
import { useTeacherStore, expenseStatusNames } from '../../stores/teacherStore';
import { useAuthStore } from '../../stores/authStore';
import FormLabel from '../../components/common/FormLabel';
import HelperText from '../../components/common/HelperText';
import { teacherExpenseHelpTexts } from '../../constants/helpTexts';
import DeleteConfirmModal from '../../components/shared/DeleteConfirmModal';
import { teacherColors as colors } from '../../styles/colors';

const statusColors: Record<number, { bg: string; text: string }> = {
  0: { bg: '#f3f4f6', text: '#6b7280' },
  1: { bg: '#fef3c7', text: '#92400e' },
  2: { bg: '#dcfce7', text: '#166534' },
  3: { bg: '#fee2e2', text: '#991b1b' },
  4: { bg: '#ede9fe', text: '#6d28d9' },
};

export default function TeacherExpenses() {
  const { expenses, setExpenses, budgetSources, setBudgetSources, categories, setCategories, isLoadingExpenses, setLoading } = useTeacherStore();
  const { user } = useAuthStore();
  const [showCreate, setShowCreate] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [filterSource, setFilterSource] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newAmount, setNewAmount] = useState('');
  const [newDate, setNewDate] = useState(new Date().toISOString().slice(0, 10));
  const [newSourceId, setNewSourceId] = useState('');
  const [newCategoryId, setNewCategoryId] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ publicId: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading('expenses', true);
    try {
      const [expRes, budgetRes, catRes] = await Promise.all([
        teacherApi.getExpenses(), teacherApi.getBudgetSources(), teacherApi.getCategories(),
      ]);
      if (expRes.success && expRes.data) setExpenses(expRes.data);
      if (budgetRes.success && budgetRes.data) setBudgetSources(budgetRes.data);
      if (catRes.success && catRes.data) setCategories(catRes.data);
    } catch (err) { console.error('Failed to load:', err); }
    finally { setLoading('expenses', false); }
  };

  const handleCreate = async () => {
    if (!newDesc.trim() || !newAmount || !newSourceId || !newDate) {
      setError('יש למלא תיאור, סכום, מקור תקציב ותאריך'); return;
    }
    setCreating(true); setError('');
    try {
      const res = await teacherApi.createExpense({
        budgetSourcePublicId: newSourceId, kindergartenPublicId: user?.kindergartenPublicId,
        categoryId: newCategoryId ? parseInt(newCategoryId) : undefined,
        description: newDesc.trim(), amount: parseFloat(newAmount),
        expenseDate: newDate, notes: newNotes.trim() || undefined,
      });
      if (res.success) { setShowCreate(false); resetForm(); await loadData(); }
      else setError(res.message || 'שגיאה');
    } catch (err: any) { setError(err.response?.data?.message || 'שגיאה'); }
    finally { setCreating(false); }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await teacherApi.deleteExpense(deleteTarget.publicId);
      if (res.success) { setDeleteTarget(null); await loadData(); }
    } catch (err) { console.error(err); }
    finally { setDeleting(false); }
  };

  const resetForm = () => { setNewDesc(''); setNewAmount(''); setNewDate(new Date().toISOString().slice(0, 10)); setNewSourceId(''); setNewCategoryId(''); setNewNotes(''); setError(''); };

  const filtered = expenses.filter(e => {
    if (filterSource && e.budgetSourcePublicId !== filterSource) return false;
    if (filterCategory && e.categoryId?.toString() !== filterCategory) return false;
    return true;
  });
  const totalFiltered = filtered.reduce((s, e) => s + e.amount, 0);
  const fmt = (n: number) => `${n.toLocaleString('he-IL')}₪`;
  const fmtDate = (s: string) => { try { return new Date(s).toLocaleDateString('he-IL'); } catch { return s; } };

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: colors.text, margin: 0 }}>הוצאות</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => setShowFilter(!showFilter)} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', backgroundColor: showFilter ? colors.primaryLight : colors.white, color: showFilter ? colors.primary : colors.textLight, border: `1px solid ${colors.border}`, borderRadius: '10px', cursor: 'pointer' }}>
            <Filter size={16} />
          </button>
          <button onClick={() => { setShowCreate(true); resetForm(); }} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', backgroundColor: colors.primary, color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
            <Plus size={18} /> הוצאה חדשה
          </button>
        </div>
      </div>

      {showFilter && (
        <div style={{ backgroundColor: colors.white, borderRadius: '12px', padding: '14px', border: `1px solid ${colors.border}`, marginBottom: '12px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <select value={filterSource} onChange={e => setFilterSource(e.target.value)} style={{ ...selectStyle, flex: 1, minWidth: '140px' }}>
            <option value="">כל המקורות</option>
            {budgetSources.map(bs => <option key={bs.publicId} value={bs.publicId}>{bs.name}</option>)}
          </select>
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} style={{ ...selectStyle, flex: 1, minWidth: '140px' }}>
            <option value="">כל הקטגוריות</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}

      {filtered.length > 0 && (
        <div style={{ fontSize: '13px', color: colors.textLight, marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
          <span>{filtered.length} הוצאות</span>
          <span style={{ fontWeight: '600', color: colors.text }}>סה״כ: {fmt(totalFiltered)}</span>
        </div>
      )}

      {isLoadingExpenses && <div style={{ textAlign: 'center', padding: '32px', color: colors.textLight }}>טוען...</div>}

      {!isLoadingExpenses && filtered.map(exp => {
        const sc = statusColors[exp.status] || statusColors[0];
        return (
          <div key={exp.publicId} style={{ backgroundColor: colors.white, borderRadius: '12px', border: `1px solid ${colors.border}`, padding: '14px 16px', marginBottom: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: colors.text }}>{exp.description}</span>
                  <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', backgroundColor: sc.bg, color: sc.text, fontWeight: '500' }}>
                    {exp.statusName || expenseStatusNames[exp.status]}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: colors.textLight }}>
                  <span>{fmtDate(exp.expenseDate)}</span>
                  <span>{exp.budgetSourceName}</span>
                  {exp.categoryName && <span>{exp.categoryName}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '15px', fontWeight: '700', color: colors.text }}>{fmt(exp.amount)}</span>
                <button onClick={() => setDeleteTarget({ publicId: exp.publicId })} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: '4px' }}>
                  <Trash2 size={14} color={colors.textLight} />
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {!isLoadingExpenses && filtered.length === 0 && (
        <div style={{ backgroundColor: colors.white, borderRadius: '14px', padding: '48px 24px', border: `1px solid ${colors.border}`, textAlign: 'center' }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '14px', backgroundColor: colors.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <Receipt size={28} color={colors.primary} />
          </div>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>אין הוצאות עדיין</h3>
          <p style={{ fontSize: '14px', color: colors.textLight }}>הוסיפי הוצאה ראשונה מתקציב קיים</p>
        </div>
      )}

      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowCreate(false); }}>
          <div style={{ backgroundColor: colors.white, borderRadius: '20px 20px 0 0', width: '100%', maxWidth: '600px', maxHeight: '85vh', overflow: 'auto', padding: '24px' }} onMouseDown={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: colors.text, margin: 0 }}>הוצאה חדשה</h2>
              <button onClick={() => setShowCreate(false)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: '4px' }}><X size={22} color={colors.textLight} /></button>
            </div>
            {error && <div style={{ backgroundColor: colors.redLight, color: '#991b1b', padding: '10px 14px', borderRadius: '10px', marginBottom: '16px', fontSize: '13px' }}>{error}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div><FormLabel text="מקור תקציב" required helpText={teacherExpenseHelpTexts.source.tooltip} style={{ color: colors.text }} />
                <select value={newSourceId} onChange={e => setNewSourceId(e.target.value)} style={inputStyle}>
                  <option value="">בחרי מקור...</option>
                  {budgetSources.filter(bs => bs.isActive).map(bs => <option key={bs.publicId} value={bs.publicId}>{bs.name} (נותר: {fmt(bs.remainingBudget)})</option>)}
                </select></div>
              <div><FormLabel text="תיאור" required helpText={teacherExpenseHelpTexts.description.tooltip} style={{ color: colors.text }} /><input value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="תיאור ההוצאה" style={inputStyle} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div><FormLabel text="סכום" required helpText={teacherExpenseHelpTexts.amount.tooltip} style={{ color: colors.text }} /><input type="number" value={newAmount} onChange={e => setNewAmount(e.target.value)} placeholder="0.00" style={inputStyle} /><HelperText text={teacherExpenseHelpTexts.amount.helper!} /></div>
                <div><FormLabel text="תאריך" required helpText={teacherExpenseHelpTexts.date.tooltip} style={{ color: colors.text }} /><input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} style={inputStyle} /></div>
              </div>
              <div><FormLabel text="קטגוריה" helpText={teacherExpenseHelpTexts.category.tooltip} style={{ color: colors.text }} />
                <select value={newCategoryId} onChange={e => setNewCategoryId(e.target.value)} style={inputStyle}>
                  <option value="">לא צוין</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
              <div><FormLabel text="הערות" helpText={teacherExpenseHelpTexts.notes.tooltip} style={{ color: colors.text }} /><textarea value={newNotes} onChange={e => setNewNotes(e.target.value)} placeholder="הערות..." rows={2} style={{ ...inputStyle, resize: 'vertical' } as React.CSSProperties} /></div>
              <button onClick={handleCreate} disabled={creating} style={{ width: '100%', padding: '14px', backgroundColor: creating ? colors.border : colors.primary, color: 'white', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '600', cursor: creating ? 'not-allowed' : 'pointer' }}>
                {creating ? 'מוסיף...' : 'הוסף הוצאה'}
              </button>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        itemName="הוצאה זו"
        isLoading={deleting}
      />
    </div>
  );
}

const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 14px', fontSize: '14px', border: '1px solid #e5e7eb', borderRadius: '10px', outline: 'none', backgroundColor: '#f9fafb', boxSizing: 'border-box', direction: 'rtl' };
const selectStyle: React.CSSProperties = { padding: '8px 12px', fontSize: '13px', border: '1px solid #e5e7eb', borderRadius: '8px', outline: 'none', backgroundColor: '#f9fafb', direction: 'rtl' };
const labelStyle: React.CSSProperties = { fontSize: '13px', fontWeight: '500', color: '#1f2937', display: 'block', marginBottom: '4px' };
